import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Heart,
  Sprout,
  TrendingUp,
} from "lucide-react";
import { fundingPaths, fundingProcess, LMI_FUNDING_URL } from "../mock";

const iconMap = { Sprout, Heart, TrendingUp };

const FundingPage = () => {
  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 75% 30%, #2dd4bf 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Sprout className="w-3.5 h-3.5" />
            <span>Funding for Kingdom-minded businesses</span>
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] max-w-4xl">
            Capital for the call.{" "}
            <em className="text-teal-300 font-serif font-normal">
              Wisdom for the walk.
            </em>
          </h1>
          <p className="mt-5 text-white/65 text-lg max-w-2xl">
            Three faith-aligned funding paths — from first-step entrepreneurs
            to expanding ministries. All applications are pre-qualified through
            our partner LMI Marketing & Business Solutions Group, Inc.
          </p>

          <div className="mt-8">
            <Link to={LMI_FUNDING_URL}>
              <Button className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-7">
                Start funding intake
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Paths */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Three funding paths
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            Find the path that{" "}
            <em className="text-teal-700 font-serif font-normal">
              fits your call.
            </em>
          </h2>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7">
            {fundingPaths.map((p, i) => {
              const Icon = iconMap[p.icon];
              const isFeatured = i === 0;
              return (
                <article
                  key={p.id}
                  className={`relative rounded-2xl p-7 md:p-8 flex flex-col ${
                    isFeatured
                      ? "bg-black text-white border border-teal-400/40"
                      : "bg-white text-black border border-black/10"
                  }`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-7 bg-teal-400 text-black text-[11px] font-semibold px-3 py-1 rounded-full tracking-wide uppercase">
                      {p.badge}
                    </span>
                  )}
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isFeatured
                        ? "bg-teal-400 text-black"
                        : "bg-teal-500/10 text-teal-700 border border-teal-600/20"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 text-2xl font-semibold tracking-tight">
                    {p.title}
                  </h3>
                  <div
                    className={`mt-1.5 text-sm font-medium ${
                      isFeatured ? "text-teal-300" : "text-teal-700"
                    }`}
                  >
                    {p.audience}
                  </div>
                  <div
                    className={`mt-4 text-xs uppercase tracking-wide font-mono ${
                      isFeatured ? "text-white/60" : "text-black/60"
                    }`}
                  >
                    Funding range:{" "}
                    <span
                      className={`font-semibold ${
                        isFeatured ? "text-white" : "text-black"
                      }`}
                    >
                      {p.range}
                    </span>
                  </div>
                  <p
                    className={`mt-4 leading-relaxed ${
                      isFeatured ? "text-white/70" : "text-black/60"
                    }`}
                  >
                    {p.summary}
                  </p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li
                        key={f}
                        className={`flex items-start gap-2.5 text-sm ${
                          isFeatured ? "text-white/85" : "text-black/70"
                        }`}
                      >
                        <span
                          className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                            isFeatured
                              ? "bg-teal-400 text-black"
                              : "bg-teal-500/15 text-teal-700"
                          }`}
                        >
                          <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={LMI_FUNDING_URL} className="mt-7">
                    <Button
                      className={`w-full font-semibold h-11 ${
                        isFeatured
                          ? "bg-teal-400 hover:bg-teal-300 text-black"
                          : "bg-black hover:bg-black/85 text-white"
                      }`}
                    >
                      Apply now
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#f6faf9] text-black py-20 md:py-28 border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            How it works
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            From application to{" "}
            <em className="text-teal-700 font-serif font-normal">funded</em> in 4 steps.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {fundingProcess.map((s) => (
              <div
                key={s.step}
                className="rounded-2xl border border-black/10 bg-white p-7 hover:border-teal-600/40 transition-colors"
              >
                <div className="text-4xl font-semibold text-teal-700 tracking-tight">
                  {s.step}
                </div>
                <h3 className="mt-3 text-xl font-semibold text-black">
                  {s.title}
                </h3>
                <p className="mt-2 text-black/60 text-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl bg-[#04181a] border border-teal-400/20 p-10 md:p-14 overflow-hidden text-center">
            <div
              className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-30"
              style={{
                background:
                  "radial-gradient(circle, #14b8a6 0%, transparent 70%)",
              }}
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] text-teal-300 font-medium">
                Take the leap
              </div>
              <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight max-w-2xl mx-auto">
                Bring the calling.{" "}
                <em className="text-teal-300 font-serif font-normal">
                  We&apos;ll bring the capital.
                </em>
              </h2>
              <p className="mt-5 text-white/65 max-w-xl mx-auto">
                Begin your prequalification with our partner LMI Marketing &
                Business Solutions Group, Inc.
              </p>
              <Link to={LMI_FUNDING_URL} className="inline-block mt-8">
                <Button className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-7">
                  Start funding intake
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="mt-4 text-xs text-white/45">
                Interim form — our LMI partner site is launching shortly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default FundingPage;
